/**
 * Registers /sw.js once at app boot, with a polite "new version ready" handler.
 *
 * - Only registers in production OR when explicitly opted-in via
 *   REACT_APP_ENABLE_SW=true, because CRA's dev server hot-reload and a
 *   live SW caching the bundle don't play well together.
 * - When a new version finishes installing, we tell it to skipWaiting and
 *   reload the page once, so users get the new bundle without nagging.
 */

export default function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  const enabled =
    process.env.NODE_ENV === 'production' ||
    (process.env.REACT_APP_ENABLE_SW || '').toLowerCase() === 'true';
  if (!enabled) return;

  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

      // Auto-update flow: when a new worker reaches "installed" while there's
      // already a controller, tell it to take over + reload exactly once.
      const handleUpdate = () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            newWorker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      };
      reg.addEventListener('updatefound', handleUpdate);
      handleUpdate();

      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      });
    } catch (err) {
      // SW failures shouldn't break the app — quietly drop into non-PWA mode.
      // eslint-disable-next-line no-console
      console.warn('SW registration failed:', err);
    }
  });
}
